
import StickyColumn from '../utils/sticky-column.js';

class ProductGrid {
  constructor() {
    this.el = document.querySelector('[js-product-grid]');
    if (!this.el) {
      return;
    }

    this.sidebarSticky = null;
    this.hasUserScaleSelection = false;
    this.currentScaleValue = null;
    this.isDesktop = this.isDesktopViewport();
    this.onResize = this.onResize.bind(this);
    this.onScaleInput = this.onScaleInput.bind(this);
    this.init();
  }

  isDesktopViewport() {
    return window.innerWidth >= 768;
  }

  isScaleViewport() {
    return window.innerWidth >= 768;
  }

  getScaleConfig() {
    const width = window.innerWidth;

    if (width >= 1920) return { min: 3, max: 7, defaultValue: 6 };
    if (width >= 1680) return { min: 3, max: 6, defaultValue: 5 };
    if (width >= 1280) return { min: 3, max: 5, defaultValue: 4 };
    if (width >= 1024) return { min: 2, max: 4, defaultValue: 3 };

    return { min: 1, max: 3, defaultValue: 2 };
  }

  isFiltersOpen() {
    return this.el.classList.contains('-filters-open');
  }

  getLenis() {
    if (typeof window === 'undefined') return null;
    return window.lenis || null;
  }

  shouldPreventLenisStart() {
    const body = document.body;
    return body.classList.contains('-minicart-open')
      || body.classList.contains('-menu-open')
      || body.classList.contains('-desktop-menu-open');
  }

  stopLenis() {
    const lenis = this.getLenis();
    if (lenis && typeof lenis.stop === 'function') {
      lenis.stop();
    }
  }

  startLenis() {
    if (this.shouldPreventLenisStart()) return;
    const lenis = this.getLenis();
    if (lenis && typeof lenis.start === 'function') {
      lenis.start();
    }
  }

  syncLenisWithFilters() {
    if (!this.isDesktopViewport() && this.isFiltersOpen()) {
      this.stopLenis();
      return;
    }
    this.startLenis();
  }

  setFiltersOpen(nextOpen) {
    this.el.classList.toggle('-filters-open', nextOpen);
    this.setupStickySidebar();
    this.syncLenisWithFilters();
    if (this.scaleInput && this.isScaleViewport()) {
      this.applyGridScale(this.currentScaleValue ?? this.scaleInput.value);
    }
  }

  applyGridScale(nextValue) {
    const { min, max } = this.getScaleConfig();
    const fallback = Number(this.scaleInput?.value) || min;
    const value = Math.max(min, Math.min(max, Number(nextValue) || fallback));
    const progress = max > min
      ? ((value - min) / (max - min)) * 100
      : 100;
    const effectiveValue = this.isFiltersOpen() && this.isDesktopViewport()
      ? Math.max(1, value - 1)
      : value;

    if (this.scaleInput) {
      this.scaleInput.min = String(min);
      this.scaleInput.max = String(max);
      if (Number(this.scaleInput.value) !== value) {
        this.scaleInput.value = String(value);
      }
      this.scaleInput.style.setProperty('--scale-progress', `${progress}%`);
    }

    this.el.style.setProperty('--grid-columns', String(value));
    this.el.style.setProperty('--grid-columns-effective', String(effectiveValue));
    if (this.scaleValue) this.scaleValue.textContent = String(value);
    this.currentScaleValue = value;
  }

  onScaleInput(event) {
    this.hasUserScaleSelection = true;
    this.applyGridScale(event.target.value);
  }

  refreshScaleControls() {
    this.scaleInput = this.el.querySelector('[js-product-grid-scale-input]');
    this.scaleValue = this.el.querySelector('[js-product-grid-scale-value]');

    if (!this.scaleInput || !this.scaleValue) return;

    if (!this.isScaleViewport()) {
      this.el.style.removeProperty('--grid-columns');
      this.el.style.removeProperty('--grid-columns-effective');
      return;
    }

    const { defaultValue } = this.getScaleConfig();
    const initialValue = this.hasUserScaleSelection
      ? (this.currentScaleValue ?? this.scaleInput.value)
      : defaultValue;
    this.applyGridScale(initialValue);
  }

  onResize() {
    const wasDesktop = this.isDesktop;
    const isDesktop = this.isDesktopViewport();

    if (!wasDesktop && isDesktop) {
      // Mobile/tablet -> desktop: always resume smooth scroll.
      this.startLenis();
    } else if (wasDesktop && !isDesktop && this.isFiltersOpen()) {
      // Desktop -> mobile/tablet: keep scroll locked if filters stay open.
      this.stopLenis();
    }

    this.isDesktop = isDesktop;
    this.syncLenisWithFilters();

    if (!this.scaleInput || !this.scaleValue) return;

    if (!this.isScaleViewport()) {
      this.el.style.removeProperty('--grid-columns');
      this.el.style.removeProperty('--grid-columns-effective');
      return;
    }

    const { defaultValue } = this.getScaleConfig();
    const nextValue = this.hasUserScaleSelection
      ? (this.currentScaleValue ?? this.scaleInput.value)
      : defaultValue;
    this.applyGridScale(nextValue);
  }

  attachEvents() {
    this.el.addEventListener('click', (e) => {
      if (e.target.closest('[js-product-grid-filters-toggle]')) {
        this.setFiltersOpen(!this.isFiltersOpen());
      }

      if (e.target.closest('[js-product-grid-filters-close]') || e.target.closest('[js-facets-close]')) {
        this.setFiltersOpen(false);
      }
    });

    this.el.addEventListener('ird:product-grid:updated', () => {
      this.setupStickySidebar();
      this.refreshScaleControls();
    });

    this.el.addEventListener('ird:product-grid:sticky-refresh', () => {
      this.sidebarSticky?.refresh();
    });

    this.el.addEventListener('input', (e) => {
      const scaleInput = e.target.closest('[js-product-grid-scale-input]');
      if (!scaleInput) return;
      this.onScaleInput({ target: scaleInput });
    });

    window.addEventListener('resize', this.onResize);
  }

  setupStickySidebar() {
    if (this.sidebarSticky) {
      this.sidebarSticky.destroy();
      this.sidebarSticky = null;
    }

    const sidebar = this.el.querySelector('.ird-product-grid__sidebar');
    if (!sidebar) return;

    this.sidebarSticky = new StickyColumn(sidebar, { offset: 88, minWidth: 768 });

    requestAnimationFrame(() => {
      this.sidebarSticky?.apply();
    });
  }

  init() {
    this.attachEvents();
    this.setupStickySidebar();
    this.syncLenisWithFilters();
    this.refreshScaleControls();
  }

}

export default ProductGrid;